import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { AlertCircle, HardDrive, Layers, Search } from 'lucide-react';
import { db } from '../../firebase';
import { EcointelLottieMark } from '../../components/EcoIntelLoader';
import { useUserFacingError } from '../../hooks/useUserFacingError';
import {
  clearAdminAnalyticsCache,
  fetchAdminDiffSiteDataWeekly,
  fetchAdminGenerateSiteDataWeekly,
  fetchAdminSynthWeeklyParcelDebug,
  fetchWeeklyScores,
} from '../../api';
import { useAdminData } from '../../components/admin/AdminLayout';

const ANALYTICS_CACHE_TARGETS = [
  { target: 'all', label: 'Clear everything', description: 'All in-memory caches plus every Firestore analytics cache document.', danger: true },
  { target: 'memory', label: 'In-memory only', description: 'Tile URL cache, response L1, pixel means, harmonic, timeline (process RAM on Cloud Run).' },
  { target: 'firestore_all', label: 'All Firestore cache collections', description: 'Every document in the analytics cache collections below (all properties).', danger: true },
  { target: 'responseCacheSummary', label: 'responseCacheSummary', description: 'Farm summary, yearly data, trajectory inputs.' },
  { target: 'responseCacheCurves', label: 'responseCacheCurves', description: 'Farm curves / weekly ensemble cache.' },
  { target: 'responseCacheWeekly', label: 'responseCacheWeekly', description: 'Weekly scoring cache.' },
  { target: 'responseCacheLandHealth', label: 'responseCacheLandHealth', description: 'Land health report payloads.' },
  { target: 'responseCacheTopo', label: 'responseCacheTopo', description: 'Topography / terrain cache.' },
  { target: 'responseCacheElevation', label: 'responseCacheElevation', description: 'Elevation cache.' },
  { target: 'harmonicModels', label: 'harmonicModels', description: 'Harmonic fit models.' },
  { target: 'fieldTimelines', label: 'fieldTimelines', description: 'Field timeline cache.' },
];

export default function CacheAdmin() {
  const { farms = [] } = useAdminData();
  const { formatError, logError } = useUserFacingError();
  const [loadingTarget, setLoadingTarget] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [inspectorFarmId, setInspectorFarmId] = useState('');
  const [inspectorParcelId, setInspectorParcelId] = useState('');
  const [inspectorYear, setInspectorYear] = useState(new Date().getFullYear() - 1);
  const [inspectorParcels, setInspectorParcels] = useState([]);
  const [inspectorLoadingParcels, setInspectorLoadingParcels] = useState(false);
  const [inspectorLoading, setInspectorLoading] = useState(false);
  const [inspectorError, setInspectorError] = useState('');
  const [canonicalPayload, setCanonicalPayload] = useState(null);
  const [canonicalRaw, setCanonicalRaw] = useState(null);
  const [canonicalSidecar, setCanonicalSidecar] = useState(null);
  const [canonicalDiff, setCanonicalDiff] = useState(null);

  const farmOptions = useMemo(() => (
    [...(farms || [])]
      .map((f) => ({ id: f.id, label: f.farmName || f.name || f.id }))
      .sort((a, b) => a.label.localeCompare(b.label))
  ), [farms]);

  useEffect(() => {
    if (!inspectorFarmId && farmOptions.length > 0) {
      setInspectorFarmId(farmOptions[0].id);
    }
  }, [farmOptions, inspectorFarmId]);

  useEffect(() => {
    let cancelled = false;
    const loadParcels = async () => {
      if (!inspectorFarmId) {
        setInspectorParcels([]);
        setInspectorParcelId('');
        return;
      }
      setInspectorLoadingParcels(true);
      try {
        const snap = await getDocs(collection(db, 'farms', inspectorFarmId, 'parcels'));
        if (cancelled) return;
        const rows = snap.docs.map((d) => {
          const data = d.data() || {};
          const key = String(data.analyticsParcelName || data.parcel_id || d.id);
          const label = data.customName || data.displayName || data.name || data.farm_field_name || key;
          return { key, label };
        }).filter((r) => r.key && r.key !== 'WHOLE_SITE');
        rows.sort((a, b) => a.label.localeCompare(b.label));
        setInspectorParcels(rows);
        setInspectorParcelId((prev) => (rows.some((r) => r.key === prev) ? prev : (rows[0]?.key || '')));
      } catch (e) {
        if (!cancelled) {
          setInspectorParcels([]);
          setInspectorParcelId('');
          setInspectorError(`Failed to load parcels: ${formatError(e)}`);
        }
      } finally {
        if (!cancelled) setInspectorLoadingParcels(false);
      }
    };
    loadParcels();
    return () => { cancelled = true; };
  }, [inspectorFarmId, formatError]);

  const summarizePayload = (payload) => {
    const weeks = Array.isArray(payload?.weeks) ? payload.weeks : [];
    const weeksWithAnyProcess = weeks
      .filter((wk) => {
        const s = wk?.scores || {};
        return [s.energy, s.water, s.mineral, s.community].some((v) => v != null);
      })
      .map((wk) => Number(wk.week))
      .filter((w) => Number.isFinite(w));
    const missingWeeks = Array.from({ length: 52 }, (_, i) => i + 1).filter((w) => !weeksWithAnyProcess.includes(w));
    return {
      weekCount: weeks.length,
      weeksWithAnyProcess,
      missingWeeks,
      synthDebug: payload?.synthDebug || {},
      assetDebug: payload?.assetDebug || {},
    };
  };

  const normalizeParcelToken = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const summarizeSidecar = (sidecarPayload) => {
    const synthWeekly = sidecarPayload?.synth_weekly && typeof sidecarPayload.synth_weekly === 'object'
      ? sidecarPayload.synth_weekly
      : {};
    const requestedNorm = normalizeParcelToken(inspectorParcelId);
    const matchingParcelId = Object.keys(synthWeekly).find((pid) => normalizeParcelToken(pid) === requestedNorm) || null;
    const field = matchingParcelId ? synthWeekly[matchingParcelId] : null;
    const weeks = Array.isArray(field?.weeks) ? field.weeks : [];
    const weeksPresent = weeks
      .map((w) => Number(w?.week))
      .filter((w) => Number.isFinite(w) && w >= 1 && w <= 52)
      .sort((a, b) => a - b);
    const uniqueWeeks = [...new Set(weeksPresent)];
    const missingWeeks = Array.from({ length: 52 }, (_, i) => i + 1).filter((w) => !uniqueWeeks.includes(w));
    return {
      fieldCount: Object.keys(synthWeekly).length,
      requestedParcelId: inspectorParcelId,
      resolvedParcelId: matchingParcelId,
      weekCount: weeks.length,
      uniqueWeekCount: uniqueWeeks.length,
      missingWeeks,
      assetPath: sidecarPayload?._meta?.assetPath || null,
      assetVariant: sidecarPayload?._meta?.assetVariant || null,
      totalGpp: field?.total_gpp ?? null,
      totalReco: field?.total_reco ?? null,
      totalNee: field?.total_nee ?? null,
      totalTco2HaYr: field?.tco2_ha_yr ?? null,
      ehiMean: field?.ehi_mean ?? null,
      edx: field?.edx ?? null,
    };
  };

  const canonicalSummary = useMemo(() => summarizePayload(canonicalPayload), [canonicalPayload]);
  const canonicalSidecarSummary = useMemo(() => summarizeSidecar(canonicalSidecar), [canonicalSidecar, inspectorParcelId]);
  const canonicalDiffSummary = useMemo(() => {
    const cmp = canonicalDiff?.comparison || {};
    const perField = cmp.perField || {};
    const field = canonicalSidecarSummary?.resolvedParcelId ? perField[canonicalSidecarSummary.resolvedParcelId] : null;
    const ehiErr = cmp.globalWeekErrors?.ehi || null;
    const efiErr = cmp.globalWeekErrors?.efi || null;
    const wciErr = cmp.globalWeekErrors?.wci || null;
    return {
      generatedFieldCount: cmp.generatedFieldCount ?? null,
      referenceFieldCount: cmp.referenceFieldCount ?? null,
      generatedOnlyFields: cmp.generatedOnlyFields || [],
      referenceOnlyFields: cmp.referenceOnlyFields || [],
      referencePath: canonicalDiff?.referencePath || null,
      ehiErr,
      efiErr,
      wciErr,
      fieldStatus: field?.status || null,
      fieldSummaryDiffKeys: field?.summaryDiff ? Object.keys(field.summaryDiff) : [],
      fieldMissingWeeksGenerated: field?.generatedMissingWeeks || [],
      fieldMissingWeeksReference: field?.referenceMissingWeeks || [],
      fieldWeekDiffSample: field?.weekDiffSample || [],
    };
  }, [canonicalDiff, canonicalSidecarSummary?.resolvedParcelId]);

  const runInspector = async () => {
    if (!inspectorFarmId || !inspectorParcelId || !inspectorYear) {
      setInspectorError('Select property, parcel, and year.');
      return;
    }
    setInspectorLoading(true);
    setInspectorError('');
    setCanonicalPayload(null);
    setCanonicalRaw(null);
    setCanonicalSidecar(null);
    setCanonicalDiff(null);
    try {
      const [canonical, canonicalRawRes, canonicalSidecarRes, canonicalDiffRes] = await Promise.all([
        fetchWeeklyScores(inspectorFarmId, inspectorParcelId, Number(inspectorYear), true, { forceRefresh: true }),
        fetchAdminSynthWeeklyParcelDebug(inspectorFarmId, inspectorParcelId, Number(inspectorYear)),
        fetchAdminGenerateSiteDataWeekly(inspectorFarmId, Number(inspectorYear)),
        fetchAdminDiffSiteDataWeekly(inspectorFarmId, Number(inspectorYear), inspectorParcelId),
      ]);
      setCanonicalPayload(canonical);
      setCanonicalRaw(canonicalRawRes);
      setCanonicalSidecar(canonicalSidecarRes);
      setCanonicalDiff(canonicalDiffRes);
    } catch (e) {
      logError('Parity inspector failed', e);
      setInspectorError(formatError(e));
    } finally {
      setInspectorLoading(false);
    }
  };

  const runClear = async (row) => {
    const { target, danger } = row;
    let msg = `Clear ${row.label}?`;
    if (danger) {
      msg = 'This removes cached analytics for all users until the server recomputes. Continue?';
    } else if (target !== 'memory') {
      msg = `Delete all documents in Firestore collection "${target}"?`;
    } else {
      msg = 'Clear in-memory caches on the analytics server?';
    }
    if (!window.confirm(msg)) return;

    setLoadingTarget(target);
    setError(null);
    setLastResult(null);
    try {
      const data = await clearAdminAnalyticsCache(target);
      setLastResult(data);
    } catch (e) {
      logError('Analytics cache clear failed', e);
      setError(formatError(e));
    } finally {
      setLoadingTarget(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="font-bold text-slate-800 mb-1 flex items-center gap-2">
          <Layers size={18} className="text-slate-500" />
          Analytics asset policy
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Frontend requests are locked to canonical assets.
        </p>
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-900">
          Canary is disabled in the web app. Admin inspector and analytics endpoints now use canonical only.
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="font-bold text-slate-800 mb-1 flex items-center gap-2">
          <Search size={18} className="text-slate-500" />
          Weekly parity inspector
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Shows canonical weekly process payloads, raw EE rows, and generated sidecar output for one parcel/year using forced refresh.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Property</label>
            <select
              value={inspectorFarmId}
              onChange={(e) => setInspectorFarmId(e.target.value)}
              className="w-full border border-slate-200 rounded-lg p-2 text-sm bg-white focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
            >
              <option value="">Select property…</option>
              {farmOptions.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Parcel</label>
            <select
              value={inspectorParcelId}
              onChange={(e) => setInspectorParcelId(e.target.value)}
              disabled={!inspectorFarmId || inspectorLoadingParcels}
              className="w-full border border-slate-200 rounded-lg p-2 text-sm bg-white focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none disabled:opacity-60"
            >
              {!inspectorFarmId && <option value="">Select property first…</option>}
              {inspectorFarmId && inspectorLoadingParcels && <option value="">Loading parcels…</option>}
              {inspectorFarmId && !inspectorLoadingParcels && inspectorParcels.length === 0 && <option value="">No parcels found</option>}
              {inspectorParcels.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Year</label>
            <input
              type="number"
              min={2018}
              max={2100}
              value={inspectorYear}
              onChange={(e) => setInspectorYear(e.target.value)}
              className="w-full border border-slate-200 rounded-lg p-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={runInspector}
              disabled={inspectorLoading || !inspectorFarmId || !inspectorParcelId}
              className="w-full px-3 py-2 text-sm font-medium rounded-lg bg-[#043132] text-white hover:bg-[#065354] disabled:opacity-50"
            >
              {inspectorLoading ? 'Running…' : 'Run canonical check'}
            </button>
          </div>
        </div>

        {inspectorError && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            {inspectorError}
          </div>
        )}

        {canonicalPayload && (
          <div className="mb-3 border border-slate-200 rounded-lg p-3">
            <div className="font-semibold text-sm text-slate-800 mb-1">Canonical weekly payload</div>
            <div className="text-[11px] text-slate-500 space-y-1">
              <div>Asset: <span className="font-mono">{canonicalSummary.assetDebug?.synthWeeklyPath || '—'}</span></div>
              <div>Requested vs resolved: <span className="font-mono">{canonicalPayload?.synthMatch?.requestedParcelId || inspectorParcelId}</span> → <span className="font-mono">{canonicalPayload?.synthMatch?.resolvedParcelId || canonicalPayload?.parcelId || '—'}</span></div>
              <div>Alias candidates: {Array.isArray(canonicalPayload?.synthMatch?.aliasCandidateParcelIds) && canonicalPayload.synthMatch.aliasCandidateParcelIds.length ? canonicalPayload.synthMatch.aliasCandidateParcelIds.join(', ') : 'n/a'}</div>
              <div>Rows selected: {canonicalSummary.synthDebug?.selectedRowCount ?? '—'} / raw: {canonicalSummary.synthDebug?.rawRowCount ?? '—'}</div>
              <div>Weeks with process values: {canonicalSummary.weeksWithAnyProcess.length}</div>
              <div>Missing weeks: {canonicalSummary.missingWeeks.length ? canonicalSummary.missingWeeks.join(', ') : 'none'}</div>
              <div>Duplicate weeks: {Array.isArray(canonicalSummary.synthDebug?.duplicateWeeks) && canonicalSummary.synthDebug.duplicateWeeks.length ? canonicalSummary.synthDebug.duplicateWeeks.join(', ') : 'none'}</div>
            </div>
          </div>
        )}

        {canonicalRaw && (
          <div className="mb-3 border border-slate-200 rounded-lg p-3">
            <div className="font-semibold text-sm text-slate-800 mb-1">Canonical raw rows</div>
            <div className="text-[11px] text-slate-500 space-y-1">
              <div>Asset: <span className="font-mono">{canonicalRaw?.assetPath || '—'}</span></div>
              <div>Resolved parcel: <span className="font-mono">{canonicalRaw?.resolvedParcelId || '—'}</span></div>
              <div>Raw rows: {canonicalRaw?.rawRowCount ?? '—'} | unique weeks: {canonicalRaw?.uniqueWeekCount ?? '—'}</div>
              <div>Weeks present: {Array.isArray(canonicalRaw?.weeksPresent) && canonicalRaw.weeksPresent.length ? canonicalRaw.weeksPresent.join(', ') : 'none'}</div>
              <div>Missing weeks: {Array.isArray(canonicalRaw?.missingWeeks) && canonicalRaw.missingWeeks.length ? canonicalRaw.missingWeeks.join(', ') : 'none'}</div>
            </div>
          </div>
        )}

        {canonicalSidecar && (
          <div className="mb-3 border border-slate-200 rounded-lg p-3">
            <div className="font-semibold text-sm text-slate-800 mb-1">Canonical generated sidecar</div>
            <div className="text-[11px] text-slate-500 space-y-1">
              <div>Asset: <span className="font-mono">{canonicalSidecarSummary.assetPath || '—'}</span></div>
              <div>Requested vs resolved: <span className="font-mono">{canonicalSidecarSummary.requestedParcelId || '—'}</span> → <span className="font-mono">{canonicalSidecarSummary.resolvedParcelId || '—'}</span></div>
              <div>Fields in payload: {canonicalSidecarSummary.fieldCount ?? '—'} | weeks: {canonicalSidecarSummary.weekCount ?? '—'} (unique {canonicalSidecarSummary.uniqueWeekCount ?? '—'})</div>
              <div>Missing weeks: {canonicalSidecarSummary.missingWeeks?.length ? canonicalSidecarSummary.missingWeeks.join(', ') : 'none'}</div>
              <div>EHI mean: {canonicalSidecarSummary.ehiMean ?? '—'} | EDX: {canonicalSidecarSummary.edx ?? '—'}</div>
              <div>Carbon totals (gC/m²/yr): GPP {canonicalSidecarSummary.totalGpp ?? '—'} / Reco {canonicalSidecarSummary.totalReco ?? '—'} / NEE {canonicalSidecarSummary.totalNee ?? '—'}</div>
              <div>tCO2e/ha/yr: {canonicalSidecarSummary.totalTco2HaYr ?? '—'}</div>
            </div>
          </div>
        )}

        {canonicalDiff && (
          <div className="mb-3 border border-slate-200 rounded-lg p-3">
            <div className="font-semibold text-sm text-slate-800 mb-1">Canonical parity diff (generated vs reference sidecar)</div>
            <div className="text-[11px] text-slate-500 space-y-1">
              <div>Reference file: <span className="font-mono">{canonicalDiffSummary.referencePath || '—'}</span></div>
              <div>Field counts: generated {canonicalDiffSummary.generatedFieldCount ?? '—'} / reference {canonicalDiffSummary.referenceFieldCount ?? '—'}</div>
              <div>Generated-only fields: {canonicalDiffSummary.generatedOnlyFields.length ? canonicalDiffSummary.generatedOnlyFields.join(', ') : 'none'}</div>
              <div>Reference-only fields: {canonicalDiffSummary.referenceOnlyFields.length ? canonicalDiffSummary.referenceOnlyFields.join(', ') : 'none'}</div>
              <div>EHI weekly error: compared {canonicalDiffSummary.ehiErr?.compared ?? 0}, MAE {canonicalDiffSummary.ehiErr?.mae ?? '—'}, max abs {canonicalDiffSummary.ehiErr?.maxAbs ?? '—'}</div>
              <div>EFI weekly error: compared {canonicalDiffSummary.efiErr?.compared ?? 0}, MAE {canonicalDiffSummary.efiErr?.mae ?? '—'}, max abs {canonicalDiffSummary.efiErr?.maxAbs ?? '—'}</div>
              <div>WCI weekly error: compared {canonicalDiffSummary.wciErr?.compared ?? 0}, MAE {canonicalDiffSummary.wciErr?.mae ?? '—'}, max abs {canonicalDiffSummary.wciErr?.maxAbs ?? '—'}</div>
              <div>Selected field status: {canonicalDiffSummary.fieldStatus || 'not present in both payloads'}</div>
              <div>Selected field summary differences: {canonicalDiffSummary.fieldSummaryDiffKeys.length ? canonicalDiffSummary.fieldSummaryDiffKeys.join(', ') : 'none'}</div>
              <div>Selected field top week diffs: {canonicalDiffSummary.fieldWeekDiffSample.length ? canonicalDiffSummary.fieldWeekDiffSample.slice(0, 8).map((d) => `w${d.week} ${d.key} Δ${Number(d.absDelta).toFixed(2)}`).join(' | ') : 'none'}</div>
              <div>Selected field missing weeks (generated): {canonicalDiffSummary.fieldMissingWeeksGenerated.length ? canonicalDiffSummary.fieldMissingWeeksGenerated.join(', ') : 'none'}</div>
              <div>Selected field missing weeks (reference): {canonicalDiffSummary.fieldMissingWeeksReference.length ? canonicalDiffSummary.fieldMissingWeeksReference.join(', ') : 'none'}</div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="font-bold text-slate-800 mb-1 flex items-center gap-2">
          <HardDrive size={18} className="text-slate-500" />
          Analytics server cache
        </h3>
        <p className="text-xs text-slate-500 mb-4 leading-relaxed">
          Targets the Cloud Run analytics API (same server as farm summaries, carbon intelligence, and land health). Clearing forces fresh Earth Engine and recomputation on the next request.
          Per-property documents under <code className="text-[10px] bg-slate-100 px-1 rounded font-mono">farms/&lt;id&gt;/cache</code> in Firestore are separate; those are cleared when you sync config or remove a parcel from a property.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        {lastResult && (
          <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-900 font-mono overflow-x-auto">
            <span className="font-sans font-semibold text-emerald-800 block mb-1">Last result (target: {lastResult.target})</span>
            {JSON.stringify(lastResult.cleared, null, 2)}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ANALYTICS_CACHE_TARGETS.map((row) => (
            <div
              key={row.target}
              className={`border rounded-lg p-4 flex flex-col gap-2 ${row.danger ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200 bg-slate-50/50'}`}
            >
              <div className="font-semibold text-sm text-slate-800">{row.label}</div>
              <p className="text-[11px] text-slate-500 flex-1 leading-snug">{row.description}</p>
              <button
                type="button"
                onClick={() => runClear(row)}
                disabled={loadingTarget != null}
                className={`mt-1 text-xs font-medium px-3 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                  row.danger
                    ? 'bg-amber-600 text-white hover:bg-amber-700'
                    : 'bg-[#043132] text-white hover:bg-[#065354]'
                }`}
              >
                {loadingTarget === row.target ? 'Clearing…' : `Clear${row.danger ? ' (all)' : ''}`}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
